import React, { useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

interface FAQAccordionProps {
  items: FAQItem[];
  className?: string;
}

const FAQAccordion: React.FC<FAQAccordionProps> = ({
  items,
  className = "",
}) => {
  const [openItem, setOpenItem] = useState<string | null>(null);

  const toggleItem = (id: string) => {
    if (openItem === id) {
      setOpenItem(null); // Ferme l'élément s'il est déjà ouvert
    } else {
      setOpenItem(id); // Ouvre le nouvel élément et ferme les autres
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {items.map((item) => {
        const isOpen = openItem === item.id;
        return (
          <div
            key={item.id}
            className="border border-slate-200 rounded-2xl bg-white shadow-sm hover:shadow-md transition-shadow duration-200 w-full"
          >
            <button
              onClick={() => toggleItem(item.id)}
              className="w-full px-6 py-5 text-left flex items-center justify-between focus:outline-none focus:ring-inset rounded-2xl cursor-pointer"
              aria-expanded={isOpen}
            >
              <h3 className="text-lg font-semibold text-slate-900 pr-4">
                {item.question}
              </h3>
              <div className="flex-shrink-0 ml-4">
                {isOpen ? (
                  <ChevronUpIcon className="h-5 w-5 text-slate-600" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-slate-600" />
                )}
              </div>
            </button>

            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                isOpen ? "opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <div className="px-6 pb-5 pt-0">
                <div className="text-slate-600 leading-relaxed prose prose-slate prose-sm max-w-none">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p: ({ children }) => (
                        <p className="mb-3 last:mb-0">{children}</p>
                      ),
                      strong: ({ children }) => (
                        <strong className="font-semibold text-slate-800">
                          {children}
                        </strong>
                      ),
                      em: ({ children }) => (
                        <em className="italic">{children}</em>
                      ),
                      ul: ({ children }) => (
                        <ul className="list-disc pl-4 mb-3 space-y-1">
                          {children}
                        </ul>
                      ),
                      ol: ({ children }) => (
                        <ol className="list-decimal pl-4 mb-3 space-y-1">
                          {children}
                        </ol>
                      ),
                      li: ({ children }) => (
                        <li className="text-slate-600">{children}</li>
                      ),
                      br: () => <br />,
                    }}
                  >
                    {item.answer}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FAQAccordion;
